import { ExploreContext } from "@/components/explore/ExploreContext";
import { TableView } from "@/components/explore/ExploreTable";
import { Loadmap } from "@/components/explore/Loadmap";
import { useMemo } from "react";

interface TrainPOVTableProps {
  zones: Array<{
    name: string;
    values: number[];
    apm_track_id?: number;
    apm_origin_station_id?: number;
    apm_destination_station_id?: number;
  }>;
  tracks: Array<{
    id: string;
    code: string;
    route: string[];
  }>;
  stations: Array<{
    id: string;
    code: string;
    name: string;
  }>;
  times: string[];
  currentIntervalIndex: number;
}

export function TrainPOVTable({
  zones,
  tracks,
  stations,
  times,
  currentIntervalIndex: _currentIntervalIndex,
}: TrainPOVTableProps) {
  const segmentsByTrack = useMemo(() => {
    const grouped: Record<string, typeof zones> = {};

    zones.forEach((zone) => {
      if (
        zone.apm_track_id &&
        zone.apm_origin_station_id &&
        zone.apm_destination_station_id
      ) {
        const trackId = zone.apm_track_id.toString();
        if (!grouped[trackId]) {
          grouped[trackId] = [];
        }

        // Check if this segment already exists (same origin + destination)
        const existingSegment = grouped[trackId].find(
          (seg) =>
            seg.apm_origin_station_id === zone.apm_origin_station_id &&
            seg.apm_destination_station_id === zone.apm_destination_station_id
        );

        if (existingSegment) {
          // Aggregate values: sum them together
          existingSegment.values = existingSegment.values.map(
            (val, idx) => val + zone.values[idx]
          );
        } else {
          // Add as new segment
          grouped[trackId].push({ ...zone });
        }
      }
    });

    return grouped;
  }, [zones]);

  const orderedSegmentsByTrack = useMemo(() => {
    const result: Record<
      string,
      {
        track: (typeof tracks)[0];
        segments: typeof zones;
      }
    > = {};

    Object.entries(segmentsByTrack).forEach(([trackId, segments]) => {
      const track = tracks.find((t) => t.id === trackId);
      if (!track) return;

      const sorted = [...segments].sort((a, b) => {
        const aOriginStation = stations.find(
          (s) => s.id === a.apm_origin_station_id?.toString()
        );
        const bOriginStation = stations.find(
          (s) => s.id === b.apm_origin_station_id?.toString()
        );

        if (!aOriginStation || !bOriginStation) return 0;

        const aOriginPos = track.route.findIndex(
          (stationId) => stationId === aOriginStation.id
        );
        const bOriginPos = track.route.findIndex(
          (stationId) => stationId === bOriginStation.id
        );

        return aOriginPos - bOriginPos;
      });

      result[trackId] = { track, segments: sorted };
    });

    return result;
  }, [segmentsByTrack, tracks, stations]);

  const getSegmentName = (segment: (typeof zones)[0]) => {
    const originStation = stations.find(
      (s) => s.id === segment.apm_origin_station_id?.toString()
    );
    const destStation = stations.find(
      (s) => s.id === segment.apm_destination_station_id?.toString()
    );

    if (!originStation || !destStation) return segment.name;

    return `${originStation.code} → ${destStation.code}`;
  };

  return (
    <div className="space-y-8 py-4">
      {Object.entries(orderedSegmentsByTrack).map(
        ([trackId, { track, segments }]) => {
          const formattedZones = segments.map((seg) => ({
            name: getSegmentName(seg),
            values: seg.values,
            id: undefined,
            zone_ids: undefined,
          }));

          return (
            <div key={trackId} className="space-y-4">
              <h3 className="text-lg font-semibold">
                {track.code} Route: Forecasted Passenger Load (Departing)
              </h3>

              <Loadmap
                segments={formattedZones}
                times={times}
                trackName={track.code}
                height={400}
              />

              <ExploreContext.Provider
                value={{
                  zones: formattedZones,
                  times: times,
                  concerns: [],
                  zoneType: "apm",
                }}
              >
                <TableView />
              </ExploreContext.Provider>
            </div>
          );
        }
      )}
    </div>
  );
}
